import { Dispatch, RefObject, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { CRFRProps, getCheckRequestFilter } from "./filter-obj";
import { getCuentasByBusqueda } from "@/services/accounts.service";
import { Option } from "@/components/select/AsyncAutocompleteSelect";
import {
  getCheckRequests,
  GetCheckRequestsProps,
} from "@/services/check-requests.service";
import { toaster } from "@/components/ui/toaster";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { AppRoutes } from "@/constants/routes";
import { NavigateFunction } from "react-router-dom";
import { AccountInfo } from "@azure/msal-browser";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("request_date", undefined);
  setValue("account_owner", "");
  setValue("sucursal", []);
  setValue("request_amount", "");
  setValue("requester", "");
  setValue("account_number", "");
  setValue("request_type", []);
  setValue("estado", []);

  submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchCheckRequests: (filters?: any) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchCheckRequests,
  setOpenFilters,
  navigate,
  fetchPermissions,
}: SubmitFiltersHandlerProps) => {
  try {
    setFiltersApplied(true);

    const filterObj: Partial<GetCheckRequestsProps> = getCheckRequestFilter(
      event as CRFRProps,
    );

    setCurrentFilters(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

    await fetchCheckRequests(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.CHECK_REQUESTS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorObj?.message ?? "No se pudieron aplicar los filtros.",
      });
    }
    throw error;
  }
};

export interface LoadOptionsHandlerProps {
  timeoutRef: RefObject<NodeJS.Timeout | null>;
  inputValue: string;
  setLoadingPersons: Dispatch<SetStateAction<boolean>>;
  getOptions: (inputValue: string) => Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  >;
}

export interface GetAccountOptionsHandlerProps {
  inputValue: string;
}

export const getOptionsHandler = async ({
  inputValue,
}: GetAccountOptionsHandlerProps) => {
  const filtered = await getCuentasByBusqueda({
    busqueda: inputValue,
  });

  return (
    filtered?.map((p) => ({
      label: `${p.titular} - ${p.nroCuenta} - ${p.tipoCuenta}`,
      value: p.nroCuenta,
      tipoCuenta: p.tipoCuenta,
      idTipoCuenta: p.idTipoCuenta,
    })) ?? []
  );
};

export const loadOptionsHandler = ({
  timeoutRef,
  inputValue,
  getOptions,
  setLoadingPersons,
}: LoadOptionsHandlerProps): Promise<Option[]> => {
  return new Promise((resolve) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef?.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (inputValue.trim().length < 3) {
        resolve([]);
        return;
      }

      setLoadingPersons(true);
      const options = getOptions(inputValue);

      setLoadingPersons(false);
      resolve(options);
    }, 1000);
  });
};

interface FetchCheckRequestsHandlersProps {
  showLoad?: boolean;
  setLoadingCheckRequests: Dispatch<SetStateAction<boolean>>;
  setCheckRequests: Dispatch<SetStateAction<any>>;
  filters?: any;
  user?: AccountInfo | null;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchCheckRequestsHandler = async ({
  showLoad,
  setLoadingCheckRequests,
  setCheckRequests,
  filters,
  user,
  navigate,
  fetchPermissions,
}: FetchCheckRequestsHandlersProps) => {
  try {
    if (showLoad) setLoadingCheckRequests(true);

    const response = await getCheckRequests({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
      usuario: user?.localAccountId,
    });

    setCheckRequests(response);

    setLoadingCheckRequests(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.message ?? "No se pudo cargar la lista de solicitudes.",
      });
    }
    throw error;
  }
};
